import { Injectable } from '@angular/core';
import { AuthRequest } from '../dtos/auth-request';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { jwtDecode } from 'jwt-decode';
import { Globals } from '../global/globals';
import { ProfileService } from './profile.service';
import { RegisterRequest } from '../dtos/register-request';
import { UserInfo } from '../dtos/user-info';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private authBaseUri: string = this.globals.backendUri + '/authentication';

  constructor(
    private httpClient: HttpClient,
    private globals: Globals,
    private profileService: ProfileService,
  ) {}

  /**
   * Login in the user. If it was successful, a valid JWT token will be stored
   *
   * @param authRequest User data
   */
  loginUser(authRequest: AuthRequest): Observable<string> {
    return this.httpClient.post(this.authBaseUri, authRequest, { responseType: 'text' }).pipe(
      tap((authResponse: string) => {
        this.setToken(authResponse);
        // refresh profile cache so header updates immediately after login
        try {
          this.profileService.getCurrentUser(true).subscribe({ next: () => {}, error: () => {} });
        } catch (err) {
          console.warn('Could not refresh profile after login', err);
        }
      }),
    );
  }

  /**
   * Register a new user. Registration does NOT log the user in: the backend returns an empty
   * body and the user must verify their email (or sign in separately) before they have a token.
   * Storing the empty response as a token would only pollute localStorage.
   *
   * @param registerRequest User data
   */
  registerUser(registerRequest: RegisterRequest): Observable<string> {
    return this.httpClient.post(this.authBaseUri + '/register', registerRequest, {
      responseType: 'text',
    });
  }

  /**
   * Check if a valid JWT token is saved in the localStorage
   */
  isLoggedIn() {
    const token = this.getToken();
    if (!token) {
      return false;
    }
    const expirationDate = this.getTokenExpirationDate(token);
    return !!expirationDate && expirationDate.valueOf() > new Date().valueOf();
  }

  logoutUser() {
    console.log('Logout');
    localStorage.removeItem('authToken');
    // clear cached profile info
    try {
      this.profileService.clearCurrentUser();
    } catch (err) {
      // ignore if profile service not available for some reason
      console.warn('Could not clear profile cache on logout', err);
    }
  }

  getToken() {
    return localStorage.getItem('authToken');
  }

  /**
   * Returns the user role from the live profile cache, falling back to the JWT.
   */
  getUserRole() {
    const liveRole = this.normalizeRole(this.profileService.getCurrentUserSnapshot()?.role);
    if (liveRole !== 'UNDEFINED') {
      return liveRole;
    }

    const token = this.getToken();
    if (token != null) {
      try {
        const decoded: any = jwtDecode(token);
        const authInfo: string[] = decoded.rol;
        if (authInfo?.includes('ROLE_SUPER_ADMIN')) {
          return 'SUPER_ADMIN';
        } else if (authInfo?.includes('ROLE_ADMIN')) {
          return 'ADMIN';
        } else if (authInfo?.includes('ROLE_USER')) {
          return 'USER';
        }
      } catch {
        // Malformed token: fall through to UNDEFINED rather than throwing.
        return 'UNDEFINED';
      }
    }
    return 'UNDEFINED';
  }

  /**
   * Returns user information from the JWT token
   */
  getUserInfo(): UserInfo | null {
    const token = this.getToken();
    if (!token) {
      return null;
    }
    try {
      const decoded: any = jwtDecode(token);
      return new UserInfo(
        '',
        decoded.sub || '',
        decoded.firstName || '',
        decoded.lastName || '',
        this.getUserRole(),
      );
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  verifyEmail(code: string) {
    return this.httpClient.post<void>(`${this.authBaseUri}/verify-email`, {
      code,
    });
  }

  requestPasswordReset(email: string): Observable<void> {
    return this.httpClient.post<void>(`${this.authBaseUri}/password-reset/request`, {
      email,
    });
  }

  resetPassword(token: string, newPassword: string): Observable<void> {
    return this.httpClient.post<void>(`${this.authBaseUri}/password-reset/confirm`, {
      token,
      newPassword,
    });
  }

  private setToken(authResponse: string) {
    localStorage.setItem('authToken', authResponse);
  }

  private getTokenExpirationDate(token: string): Date | null {
    let decoded: any;
    try {
      decoded = jwtDecode(token);
    } catch {
      // A malformed/corrupt token is treated as logged-out instead of throwing
      // (this runs inside isLoggedIn(), which the AuthGuard and header rely on).
      return null;
    }
    if (decoded.exp === undefined) {
      return null;
    }

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  private normalizeRole(role?: string): string {
    if (!role) {
      return 'UNDEFINED';
    }

    const normalizedRole = role.replace(/^ROLE_/, '');
    return ['SUPER_ADMIN', 'ADMIN', 'USER'].includes(normalizedRole) ? normalizedRole : 'UNDEFINED';
  }
}
